import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import httpMethod from 'services/update2025/httpMethod';

import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";

import { findIdpEventLogs } from "services/IdpEventLogService";
import { IdpEventLogContext } from "components/idp-event-log/IdpEventLogContext";
import { X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";
import moment from "moment";

export const IdpEventLogList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(['idp-event-log', "common"])
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language
    }

    const idpEventLogContext = useContext(IdpEventLogContext);
    const {
        idpEventLogs,
        setIdpEventLogs,
    } = idpEventLogContext;

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [startTime, setStartTime] = useState(moment().add(-7, 'd'));
    const [endTime, setEndTime] = useState(moment());
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            "type": { value: "", matchMode: 'contains' },
            "realmId": { value: "", matchMode: 'contains' },
            "clientId": { value: "", matchMode: 'contains' },
            "ipAddress": { value: "", matchMode: 'contains' },
            "startTime": { value: startTime.format('DD/MM/YYYY HH:mm:ss'), matchMode: 'gte' },
            "endTime": { value: endTime.format('DD/MM/YYYY HH:mm:ss'), matchMode: 'lte' }
        }
    });
    const [globalFilter] = useState(null);

    useEffect(() => {
        loadLazyData();
    }, [lazyParams]);

    const loadLazyData = () => {
        setLoading(true);

        findIdpEventLogs(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                setIdpEventLogs(res.data);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => {
                setLoading(false)
            });
    };

    const onPage = (event) => {
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    }

    const onSort = (event) => {
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    }

    const onFilter = (event) => {
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams['first'] = 0;
        setLazyParams(_lazyParams);
    }

    const onDateChange = (e, name) => {
        const date = e.target?.value || e.value || new Date;
        if (name === 'startTime') {
            setStartTime(moment(date));
        } else if (name === 'endTime') {
            setEndTime(moment(date));
        }

        const val = moment(date).format('DD/MM/YYYY HH:mm:ss');
        let _lazyParams = { ...lazyParams };
        _lazyParams['filters'][`${name}`]['value'] = val;

        setLazyParams(_lazyParams);
    }

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target?.value || "")} />
            </div>
        )
    };

    const timeBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex">
                    <p>{moment(rowData.time).format('DD/MM/YYYY HH:mm:ss')}</p>
                </div>
            </>
        );
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("idpEventLog.list")}</h5>
            <span className="block mt-2 md:mt-0">
                <span>
                    <label htmlFor="startTime">{t("startTime")}</label>
                    <Calendar id="startTime" dateFormat="dd/mm/yy" value={startTime.toDate()} showIcon={true}
                        onChange={(e) => onDateChange(e, "startTime")} style={{ marginLeft: '10px' }}></Calendar>
                </span>
                <span style={{ marginLeft: '10px' }}>
                    <label htmlFor="endTime">{t("endTime")}</label>
                    <Calendar id="endTime" dateFormat="dd/mm/yy" value={endTime.toDate()} showIcon={true}
                        onChange={(e) => onDateChange(e, "endTime")} style={{ marginLeft: '10px' }}></Calendar>
                </span>
            </span>
        </div>
    );

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />

                    <DataTable ref={dt} value={idpEventLogs}
                        dataKey="id" className="datatable-responsive p-datatable-sm"
                        globalFilter={globalFilter} header={headerTemplate} showGridlines lazy
                        paginator rows={lazyParams.rows} rowsPerPageOptions={[10, 20, 50]}
                        first={lazyParams.first} totalRecords={totalRecords} onPage={onPage}
                        onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                        onFilter={onFilter} filters={lazyParams.filters} loading={loading}>
                        <Column field="realmId" header={t("realmId")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} />
                        <Column field="clientId" header={t("clientId")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} />
                        <Column field="ipAddress" header={t("ipAddress")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} />
                        <Column field="type" header={t("type")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} />
                        <Column field="time" header={t("time")} sortable filterElement={textFilterTemplate} body={timeBodyTemplate} />
                        <Column field="userId" header={t("userId")} filterElement={textFilterTemplate} />
                        <Column field="sessionId" header={t("sessionId")} filterElement={textFilterTemplate} />
                    </DataTable>
                </div>
            </div>
        </div>
    )
}
